/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { INodeProps, Module, Node, isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ISearchCategoryViewProps, ISearchFormViewProps, ISearchKeywordViewProps, ISearchProductViewProps } from './components';
import { ISearchViewProps } from './dlvry-search';
import { useBlackoutOverlay } from './components/dlvry-search.hooks';

const SearchView: React.FC<ISearchViewProps> = props => {
    const {
        context,
        Search,
        AutoSuggestAriaLabel,
        AutoSuggestAriaLabelText,
        searchText,
        AutoSuggest,
        KeywordSuggest,
        ProductSuggest,
        CategorySuggest,
        UlKeyword,
        UlProduct,
        UlCategory,
        form,
        autosuggestCategory,
        autosuggestKeyword,
        autosuggestProduct,
        SearchForm,
        FormWrapper,
        label,
        isLoadingAutoSuggest,
        isLoadingNode,
        isSearchFormExpanded,
        searchTermCompleted,
        callbacks: { handleCancelSearchFocused }
    } = props;

    const [isMobilePort, setIsMobile] = React.useState<boolean>(false);
    const overlayDivRef = React.useRef<HTMLDivElement | null>(null);
    const searchDivRef = React.useRef<HTMLDivElement | null>(null);
    const [
        shouldShowOverlay,
        popupPosition,
        setIsInputFocused
    ] = useBlackoutOverlay(props, searchDivRef, isMobilePort, Boolean(searchText));

    React.useEffect(() => {
        const onResize = () => {
            const isMobileView = isMobile({ variant: VariantType.Browser, context: context.request }) === 'xs';
            setIsMobile(isMobileView);
        };
        onResize(); // Call to set initial value

        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [context]);

    const viewport = isMobile({ variant: VariantType.Browser, context: context.request });
    const isMobileView = viewport === 'sm' || viewport === 'xs';

    return (
        <>
        <Module {...Search} data-focused={shouldShowOverlay ? true : undefined} ref={searchDivRef}>
            {label}
            {_renderForm(form as ISearchFormViewProps, SearchForm, FormWrapper, isMobilePort, searchText, searchTermCompleted)}

            {isMobileView && (
                <Node {...AutoSuggest} style={popupPosition}>
                    {searchText && searchText.length > 0 ? <Node {...AutoSuggestAriaLabel}>{AutoSuggestAriaLabelText}</Node> : ''}

                    {isLoadingAutoSuggest || searchTermCompleted ? (
                        <Node className='msc-autoSuggest__results-container'>
                            {_renderKeywordSuggestions(KeywordSuggest, UlKeyword, autosuggestKeyword, isLoadingAutoSuggest, isLoadingNode)}
                            {_renderCategorySuggestions(CategorySuggest, UlCategory, autosuggestCategory, isLoadingAutoSuggest, isLoadingNode)}
                            {_renderProductSuggestions(ProductSuggest, UlProduct, autosuggestProduct, isLoadingAutoSuggest, isLoadingNode)}
                        </Node>
                    ) : null}
                </Node>
            )}
            {!isMobileView && (AutoSuggest.className.includes('show') || isSearchFormExpanded) && (
                <Node {...AutoSuggest} style={popupPosition}>
                    {searchText && searchText.length > 0 ? <Node {...AutoSuggestAriaLabel}>{AutoSuggestAriaLabelText}</Node> : ''}

                    {searchTermCompleted && (
                        <Node className='msc-autoSuggest__results-container'>
                            {_renderKeywordSuggestions(KeywordSuggest, UlKeyword, autosuggestKeyword, isLoadingAutoSuggest, isLoadingNode)}
                            {_renderCategorySuggestions(CategorySuggest, UlCategory, autosuggestCategory, isLoadingAutoSuggest, isLoadingNode)}
                            {_renderProductSuggestions(ProductSuggest, UlProduct, autosuggestProduct, isLoadingAutoSuggest, isLoadingNode)}
                        </Node>
                    )}
                </Node>
            )}
        </Module>
        {shouldShowOverlay && (
            <div ref={overlayDivRef} className='ms-search__overlay' onClick={(event) => {
                if (event.target === overlayDivRef.current) {
                    setIsInputFocused(false);
                    handleCancelSearchFocused?.();
                }
            }} />
        )}
        </>
    );
};

const _renderForm = (
    form: ISearchFormViewProps,
    SearchForm: INodeProps,
    FormWrapper: INodeProps,
    isMobilePort: boolean,
    searchText: string | undefined,
    searchTermCompleted: string | undefined
) => {
    return (
        <Node {...SearchForm}>
            <Node {...FormWrapper}>
                {isMobilePort ? (
                    <>
                    {form.input}
                    {form.cancelBtn}
                    </>
                ) : (
                    <>
                    {!searchText && form.submitBtn}
                    {form.input}
                    {searchText && form.cancelBtn}
                    {searchText && form.submitBtn}
                    </>
                )}
            </Node>
        </Node>
    );
};

const _renderKeywordSuggestions = (
    KeywordSuggest: INodeProps,
    UlKeyword: INodeProps,
    keywordSuggestions?: ISearchKeywordViewProps,
    isLoadingAutoSuggest?: boolean,
    isLoadingNode?: React.ReactNode
) => {
    return (
        keywordSuggestions && (
            <Node {...KeywordSuggest}>
                <Node {...UlKeyword}>
                    {/* {isLoadingAutoSuggest && isLoadingNode} */}
                    {!isLoadingAutoSuggest &&
                        keywordSuggestions.text.map(text => {
                            return text;
                        })}
                </Node>
            </Node>
        )
    );
};

const _renderProductSuggestions = (
    ProductSuggest: INodeProps,
    UlProduct: INodeProps,
    productSuggestions?: ISearchProductViewProps,
    isLoadingAutoSuggest?: boolean,
    isLoadingNode?: React.ReactNode
) => {
    return (
        productSuggestions && (
            <Node {...ProductSuggest}>
                <Node {...UlProduct}>
                    {productSuggestions.title}
                    {isLoadingAutoSuggest && isLoadingNode}
                    {!isLoadingAutoSuggest &&
                        productSuggestions.items.map((item, index) => {
                            return (
                                <Node {...item.LiProduct} key={item.id || index}>
                                    <Node {...item.AProduct}>
                                        {item.thumbnail}
                                        <div className='msc-autoSuggest__productResults-item-info'>
                                            {item.text}
                                            <div className='msc-autoSuggest__productResults-item-info-bottom'>
                                                {item.price}
                                                {item.buySection}
                                            </div>
                                        </div>
                                    </Node>
                                </Node>
                            );
                        })}
                </Node>
            </Node>
        )
    );
};

const _renderCategorySuggestions = (
    CategorySuggest: INodeProps,
    UlCategory: INodeProps,
    categorySuggestions?: ISearchCategoryViewProps,
    isLoadingAutoSuggest?: boolean,
    isLoadingNode?: React.ReactNode
) => {
    return (
        categorySuggestions && (
            <Node {...CategorySuggest}>
                <Node {...UlCategory}>
                    {categorySuggestions.title}
                    {isLoadingAutoSuggest && isLoadingNode}
                    {!isLoadingAutoSuggest &&
                        categorySuggestions.text.map(text => {
                            return text;
                        })}
                </Node>
            </Node>
        )
    );
};

export default SearchView;
